import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  Employee,
  CreateEmployee,
  UpdateEmployee,
  EmployeerStatus,
} from '../../../models/employee.model';
import { EmployeeService } from '../../../services/employee.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ToolbarModule } from "primeng/toolbar";

@Component({
  selector: 'app-employee-create-or-update',
  standalone: true,
  templateUrl: './employee-create-or-update.component.html',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToastModule,
    ToolbarModule
],
})
export class EmployeeCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private employeeService = inject(EmployeeService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    firstName: [null, [Validators.required, Validators.maxLength(255)]],
    lastName: [null, [Validators.required, Validators.maxLength(255)]],
    email: [null, [Validators.required, Validators.maxLength(255)]],
    status: [EmployeerStatus.Active, [Validators.required]],
  });

  employeeId = signal<string | null>(null);
  loading = signal(false);

  statusOptions = this.employeeService.getEmployeerStatusOptions();

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.employeeId.set(id);
      this.loadData(id);
    }
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    const id = this.employeeId();
    const data = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      request$ = this.employeeService.update(id, data as UpdateEmployee);
    } else {
      delete (data as any).id;
      request$ = this.employeeService.create(data as CreateEmployee);
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Colaborador salvo com sucesso',
          life: 2000,
        });

        this.router.navigate(['/employees']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (e.error.message) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar o colaborador',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.employeeService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const data = response.data as Employee;
        this.formGroup.patchValue(data);
        this.formGroup.get('status')?.setValue(EmployeerStatus[data.status]);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o colaborador',
            life: 3000,
          });
        }
      },
    });
  }
}
